import type { ArcadeScreen } from "../ScreenManager.js";
import type { MenuAction } from "../MenuInput.js";
import { Reparenter } from "../reparent.js";
import { NavList, type NavItem } from "../navList.js";
import { uiSfx } from "../uiSfx.js";

/**
 * GARAGE — first-class re-housing of the classic VEHICLE + UPGRADES tabs
 * (docs/menu-redesign-plan.md §3.6).
 *
 * Nothing is rebuilt: the live showcase canvas, info panel, carousel nav,
 * AI-builder buttons, credit balance and both upgrade sections are ADOPTED
 * from the classic tab panes into this screen's grid on enter and restored on
 * leave (Reparenter), so every listener, WebGL context and piece of upgrade/
 * auth logic in VehicleSelect keeps running untouched. The only new chrome is
 * the left-hand vehicle list (pure presentation over `vehicleListInfo()`)
 * and the guest sign-in prompt.
 *
 * Nav: ↑/↓ list + action buttons, ◄/► cycles the carousel, Enter/Ⓐ selects,
 * Esc/Ⓑ closes the AI modal if open, else back to MAIN MENU.
 */

export interface GarageSelectFacade {
  garageEnter(): void;
  garageLeave(): void;
  vehicleListInfo(): Array<{ id: string; name: string; trim: string; custom: boolean; selected: boolean }>;
  selectVehicleAt(idx: number): void;
  cycleSelection(delta: number): void;
  setVehiclesChangedHook(fn: (() => void) | null): void;
}

export interface GarageDeps {
  select: GarageSelectFacade;
  isGuest: () => boolean;
  /** Guest pressed the locked-upgrades prompt → OPTIONS / ACCOUNT. */
  onSignIn: () => void;
  onBack: () => void;
  /** Is a floating overlay (AI modal) covering the screen right now? */
  overlayOpen: () => boolean;
  /** Close it. Returns true when one was open. */
  closeOverlay: () => boolean;
}

/** Classic element id → selector of the slot it docks into while here. */
const ADOPTIONS: ReadonlyArray<readonly [id: string, slot: string]> = [
  ["vehicle-preview-canvas", ".gr-stage"],
  ["vehicle-showcase-nav", ".gr-slot-nav"],
  ["vehicle-info-panel", ".gr-slot-info"],
  ["vehicle-custom-row", ".gr-slot-custom"],
  ["credit-balance", ".gr-slot-credit"],
  ["preset-section", ".gr-slot-preset"],
  ["weapon-section", ".gr-slot-weapon"],
];

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

export class GarageScreen implements ArcadeScreen {
  private root: HTMLElement | null = null;
  private listEl: HTMLElement | null = null;
  private signinEl: HTMLButtonElement | null = null;
  private readonly adopter = new Reparenter();
  private readonly nav = new NavList(() => uiSfx.play("nav"));
  private active = false;

  constructor(private readonly deps: GarageDeps) {}

  mount(root: HTMLElement): void {
    this.root = root;
    root.innerHTML = `
      <div class="gr-wrap">
        <div class="gr-head">
          <span class="gr-eyebrow">GARAGE</span>
          <span class="gr-headrule" aria-hidden="true"></span>
        </div>
        <div class="gr-cols">
          <div class="gr-left">
            <div class="gr-panel-label">VEHICLES</div>
            <div class="gr-list" role="listbox" aria-label="Vehicles"></div>
            <div class="gr-slot gr-slot-custom"></div>
          </div>
          <div class="gr-center">
            <div class="gr-stage"></div>
            <div class="gr-slot gr-slot-nav"></div>
          </div>
          <div class="gr-right">
            <div class="gr-slot gr-slot-info"></div>
            <div class="gr-panel-label gr-upgrades-label">UPGRADES</div>
            <div class="gr-slot gr-slot-credit"></div>
            <button type="button" class="gr-signin hidden">SIGN IN TO UNLOCK UPGRADES ▸</button>
          </div>
        </div>
        <div class="gr-upgrades">
          <div class="gr-slot gr-slot-preset"></div>
          <div class="gr-slot gr-slot-weapon"></div>
        </div>
        <div class="gr-footer">
          <span class="gr-hints">↑↓ SELECT &nbsp;·&nbsp; ◄ ► CYCLE RIDE &nbsp;·&nbsp; ENTER / Ⓐ CONFIRM &nbsp;·&nbsp; ESC / Ⓑ BACK</span>
        </div>
      </div>`;

    this.listEl = root.querySelector(".gr-list");
    this.signinEl = root.querySelector(".gr-signin");
    this.signinEl?.addEventListener("click", () => {
      uiSfx.play("confirm");
      this.deps.onSignIn();
    });
  }

  onEnter(): void {
    this.active = true;
    this.adoptAll();
    this.deps.select.setVehiclesChangedHook(() => {
      if (this.active) this.renderList();
    });
    this.deps.select.garageEnter();
    this.signinEl?.classList.toggle("hidden", !this.deps.isGuest());
    this.renderList();
  }

  onLeave(): void {
    this.active = false;
    this.deps.select.setVehiclesChangedHook(null);
    this.deps.select.garageLeave();
    this.nav.clearFocus();
    this.adopter.releaseAll();
  }

  handleInput(action: MenuAction): boolean {
    // While the AI-builder modal is up it owns the screen: Esc/Ⓑ closes it,
    // everything else is swallowed so pad presses can't drive the garage
    // underneath (typing stays native — MenuInput skips editable targets).
    if (this.deps.overlayOpen()) {
      if (action.type === "back") {
        this.deps.closeOverlay();
        uiSfx.play("back");
      }
      return true;
    }
    switch (action.type) {
      case "up":
        this.nav.move(-1);
        return true;
      case "down":
        this.nav.move(1);
        return true;
      case "left":
        this.deps.select.cycleSelection(-1);
        uiSfx.play("nav");
        return true;
      case "right":
        this.deps.select.cycleSelection(1);
        uiSfx.play("nav");
        return true;
      case "confirm":
        if (this.nav.activate()) uiSfx.play("confirm");
        return true;
      case "back":
        uiSfx.play("back");
        this.deps.onBack();
        return true;
      default:
        return false;
    }
  }

  // ── internals ───────────────────────────────────────────────────────────

  private adoptAll(): void {
    const root = this.root;
    if (!root) return;
    for (const [id, slotSel] of ADOPTIONS) {
      const el = document.getElementById(id);
      const slot = root.querySelector<HTMLElement>(slotSel);
      if (el && slot) this.adopter.claim(el, slot);
    }
  }

  /** Left-hand vehicle list — presentation only; selection state lives in VehicleSelect. */
  private renderList(): void {
    const listEl = this.listEl;
    if (!listEl) return;
    const info = this.deps.select.vehicleListInfo();
    listEl.innerHTML = "";
    const items: NavItem[] = [];

    info.forEach((v, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `gr-item${v.selected ? " is-selected" : ""}`;
      btn.setAttribute("role", "option");
      btn.setAttribute("aria-selected", v.selected ? "true" : "false");
      btn.style.setProperty("--trim", v.trim);
      btn.innerHTML =
        `<span class="gr-item-dot" aria-hidden="true">◉</span>` +
        `<span class="gr-item-name">${escapeHtml(v.name)}</span>` +
        (v.custom ? `<span class="gr-item-badge">CUSTOM</span>` : "");
      btn.addEventListener("mouseenter", () => this.nav.focusTo(items.findIndex((it) => it.el === btn), true));
      btn.addEventListener("click", () => this.deps.select.selectVehicleAt(i));
      listEl.appendChild(btn);
      items.push({ el: btn, activate: () => this.deps.select.selectVehicleAt(i) });
    });

    // Adopted action buttons join the same focus ring when visible.
    for (const id of ["ai-builder-btn", "vehicle-edit-style-btn", "vehicle-delete-btn"]) {
      const el = document.getElementById(id) as HTMLButtonElement | null;
      if (el && !el.classList.contains("hidden")) {
        items.push({ el, activate: () => el.click() });
      }
    }
    if (this.signinEl && !this.signinEl.classList.contains("hidden")) {
      const chip = this.signinEl;
      items.push({ el: chip, activate: () => chip.click() });
    }

    this.nav.setItems(items);
  }
}
